import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { UserType } from '../lib/auth/types';
import { useApi } from '../lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from "sonner";
import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Skeleton } from "@pagespeedify/ui";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@pagespeedify/ui";

import { CreateTeamForm } from '../components/teams/CreateTeamForm';
import { TeamManagementView } from '../components/teams/TeamManagementView';

import {
  ApiResponse,
  Team,
  TeamMember,
  TeamListData,
  TeamMemberAddData,
  TeamUpdateData // <-- Add this if not present
} from '../types';

export default function TeamsAdminPage() {
  const { userType, user } = useAuth();
  const apiClient = useApi();
  const rqQueryClient = useQueryClient();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  if (userType !== UserType.ENTERPRISE && userType !== UserType.ADMIN) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Team Management</h1>
        <p className="text-red-600">This feature is only available for enterprise accounts.</p>
      </div>
    );
  }

  const { data: currentTeam, isLoading: isLoadingTeams, isError: isTeamsError, error: teamsErrorObj, refetch: refetchTeams } =
    useQuery<ApiResponse<Team[]>, Error, Team | null>({
      queryKey: ['userTeams', user?.uid],
      queryFn: () => apiClient.get('/teams'),
      enabled: !!user?.uid,
      staleTime: 5 * 60 * 1000,
      select: (response) => {
        const teamsArray = response?.success ? response.data : null;
        const team = teamsArray && Array.isArray(teamsArray) && teamsArray.length > 0
          ? teamsArray[0]
          : null;
        return team;
      },
    });

  const { data: membersResponse, isLoading: isLoadingMembers, isError: isMembersError, error: membersErrorObj, refetch: refetchMembers } =
    useQuery<ApiResponse<TeamMember[]>, Error>({
      queryKey: ['teamMembers', currentTeam?.id],
      queryFn: () => {
        if (!currentTeam?.id) return Promise.reject(new Error("No team ID"));
        return apiClient.get(`/teams/${currentTeam.id}/members`);
      },
      enabled: !!currentTeam?.id,
      staleTime: 15 * 60 * 1000, // 15 minutes
      onSuccess: (data) => {},
      onError: (error) => {
        console.error('[Query onError teamMembers] Failed:', error);
      }
    });

  const createTeamMutation = useMutation<ApiResponse<Team>, Error, { name: string }>({
    mutationFn: (data) => apiClient.post('/teams', { name: data.name }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Team created successfully!');
        rqQueryClient.invalidateQueries({ queryKey: ['userTeams', user?.uid] });
      } else {
        toast.error(response.message || response.error || 'Failed to create team.');
      }
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unknown error creating team';
      toast.error(`Error: ${errorMsg}`);
    },
  });

  const addMemberMutation = useMutation<ApiResponse<TeamMemberAddData>, Error, { teamId: string; email: string }>({
    mutationFn: (data) => apiClient.post(`/teams/${data.teamId}/members`, { email: data.email }),
    onSuccess: (response, variables) => {
      if (response.success) {
        toast.success(response.data?.message || `Member ${variables.email} added successfully!`);
        rqQueryClient.invalidateQueries({ queryKey: ['teamMembers', variables.teamId] });
        rqQueryClient.invalidateQueries({ queryKey: ['userTeams', user?.uid] });
      } else {
        toast.error(response.message || response.error || 'Failed to add member.', {
          duration: 30000,
          closeButton: true,
        });
      }
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unknown error adding member';
      toast.error(`Error: ${errorMsg}`, {
        duration: 30000,
        closeButton: true,
      });
    }
  });

  const removeMemberMutation = useMutation<ApiResponse<null>, Error, { teamId: string; memberId: string }>({
    mutationFn: (data) => apiClient.delete(`/teams/${data.teamId}/members/${data.memberId}`),
    onSuccess: (response, variables) => {
      toast.success('Member removed successfully!');
      rqQueryClient.invalidateQueries({ queryKey: ['teamMembers', variables.teamId] });
      rqQueryClient.invalidateQueries({ queryKey: ['userTeams', user?.uid] });
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unknown error removing member';
      toast.error(`Error: ${errorMsg}`);
    },
    onSettled: () => {
      setRemovingMemberId(null);
      setShowRemoveConfirm(false);
    }
  });

  const renameTeamMutation = useMutation<ApiResponse<TeamUpdateData>, Error, { teamId: string; name: string }>({
    mutationFn: (data) => apiClient.patch(`/teams/${data.teamId}`, { name: data.name }),
    onSuccess: (response, variables) => {
      if (response.success) {
        toast.success('Team renamed successfully!');
        rqQueryClient.invalidateQueries({ queryKey: ['userTeams', user?.uid] });
      } else {
        toast.error(response.message || response.error || 'Failed to rename team.');
      }
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unknown error renaming team';
      toast.error(`Error: ${errorMsg}`);
    }
  });

  const handleCreateTeamSubmit = (teamName: string) => {
    createTeamMutation.mutate({ name: teamName });
  };

  const handleAddMemberSubmit = (email: string) => {
    if (!currentTeam) {
      toast.error("Cannot add member: No team selected.");
      return;
    }

    const currentMemberCount = membersResponse?.data?.length ?? 0;

    if (currentMemberCount >= 4) {
      toast.error("Error: Team has already reached the maximum limit of 4 members.", {
        duration: 30000,
        closeButton: true,
      });
      return;
    }

    addMemberMutation.mutate({ teamId: currentTeam.id, email: email });
  };

  const handleRemoveMemberClick = (memberId: string) => {
    setRemovingMemberId(memberId);
    setShowRemoveConfirm(true);
  };

  const handleRemoveMemberConfirm = () => {
    if (!currentTeam || !removingMemberId) return;
    removeMemberMutation.mutate({ teamId: currentTeam.id, memberId: removingMemberId });
  };

  const handleRenameTeamSubmit = (data: { teamId: string; name: string }) => {
    renameTeamMutation.mutate(data);
  };

  const handleDeleteTeamClick = () => {
    toast.info("Deleting teams is currently disabled.");
  };

  const isLoading = isLoadingTeams;
  const hasError = isTeamsError;
  const errorMessage = teamsErrorObj?.message || 'An error occurred while loading team data.';

  if (isLoading) {
    return (
      <div className="p-4 space-y-6">
        <h1 className="text-2xl font-bold">Manage Your Team</h1>
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-48 w-full max-w-md" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (hasError && !currentTeam) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Manage Your Team</h1>
        <Card className="border-red-500">
          <CardHeader><CardTitle className="text-red-600">Error Loading Team Data</CardTitle></CardHeader>
          <CardContent>
            <p>{errorMessage}</p>
            <Button onClick={() => refetchTeams()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-bold">Manage Your Team</h1>
      <p>Welcome, {user?.email}.</p>

      {!currentTeam ? (
        <CreateTeamForm
          onSubmit={handleCreateTeamSubmit}
          isCreating={createTeamMutation.isPending}
        />
      ) : (
        <TeamManagementView
          team={currentTeam}
          members={membersResponse?.data}
          isLoadingMembers={isLoadingMembers}
          isMembersError={isMembersError || (membersResponse !== undefined && !membersResponse.success)}
          membersError={membersErrorObj || (membersResponse !== undefined && !membersResponse.success ? new Error(membersResponse.message || 'Error fetching members') : null)}
          onAddMember={handleAddMemberSubmit}
          onRemoveMember={handleRemoveMemberClick}
          onRenameTeam={handleRenameTeamSubmit}
          isRenaming={renameTeamMutation.isPending}
          isAddingMember={addMemberMutation.isPending}
          isRemovingMemberId={removeMemberMutation.isPending ? removingMemberId : null}
          refetchMembers={refetchMembers}
        />
      )}

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently remove the member from your team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemovingMemberId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMemberConfirm} disabled={removeMemberMutation.isPending}>
              {removeMemberMutation.isPending ? 'Removing...' : 'Confirm Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}